import { waitFor } from 'xstate';

import { CONSENT_COOKIE_NAME } from 'constants/cookies';
import { ValidationError } from 'errors';
import type { ValidationErrorsResponse } from 'models/api';
import { getConsentHeader } from 'utils/consent';
import { getCookie } from 'utils/cookies';
import { is, isClient, sendGlobalEvent } from 'utils/helpers';
import { log } from 'utils/log';
import { type Result, toResult } from 'utils/result';

import {
	type AllowEmptyOptions,
	type DisallowEmptyOptions,
	type FetchError,
	type FetchInput,
	type FetchResponse,
	fetchResponse,
	type RequestOptions,
	type ResponseOptions,
	unwrapResponse,
} from './core';
import { getCloudflareAccessHeaders, mergeHeaders } from './headers';

/** How many times to attempt a request that keeps coming back as 401. */
const MAX_ATTEMPTS = 3;

/** Ensure the user has a valid token. */
export async function ensureFreshToken() {
	if (isClient() && globalThis.globalFetchLockActor) {
		const tokenExpiry = globalThis.localStorage.getItem('tokenExpiry');
		if (!tokenExpiry || Date.now() > Number.parseInt(tokenExpiry, 10)) {
			sendGlobalEvent('refresh-token');
		}
		await waitFor(
			globalThis.globalFetchLockActor,
			(state) => state.matches('unlocked'),
			{ timeout: 20_000 },
		);
	}
}

/**
 * Kept out of the Digital Platform API response caches.
 *
 * Merged under the caller headers rather than over them, so a request that
 * wants caching can ask for it.
 */
const NO_CACHE_HEADERS = {
	'Cache-Control': 'no-cache',
	'Pragma': 'no-cache',
};

function getDpApiHeaders() {
	return {
		...getCloudflareAccessHeaders(),
		...getConsentHeader(getCookie(CONSENT_COOKIE_NAME)),
	};
}

function isValidationErrorsResponse(
	data: unknown,
): data is ValidationErrorsResponse {
	return is.object(data) && 'errors' in data && is.array(data.errors);
}

/**
 * Do a Digital Platform API request and get the parsed response, status
 * and headers.
 *
 * Waits for a fresh auth token before each attempt and retries 401 responses
 * after a token refresh, ending the session when that keeps failing. A non-ok
 * status resolves rather than throws, so validation errors arrive as plain
 * data here.
 */
export function fetchDpApiResponse<T = unknown>(
	input: FetchInput,
	options?: ResponseOptions,
): Promise<FetchResponse<T>> {
	// Recursing in a closure keeps the attempt count off the signature.
	const attempt = async (count: number): Promise<FetchResponse<T>> => {
		await ensureFreshToken();

		const response = await fetchResponse<T>(input, {
			...options,
			cache: options?.cache ?? 'no-store',
			headers: mergeHeaders(
				NO_CACHE_HEADERS,
				options?.headers,
				getDpApiHeaders(),
			),
		});

		if (response.status !== 401) {
			return response;
		}

		// For 401 Unauthorized responses, try to refresh the user's access
		// token and run the request again.
		if (count < MAX_ATTEMPTS) {
			sendGlobalEvent('refresh-token');
			return attempt(count + 1);
		}

		sendGlobalEvent('force-end-session');
		log.warning(
			`401 Unauthorized at ${input.toString()}: refreshing token and retrying request failed after ${MAX_ATTEMPTS} attempts, logging out user`,
		);
		return response;
	};

	return attempt(1);
}

/**
 * Do a Digital Platform API request and get the parsed response, throwing
 * on a non-ok or empty response.
 *
 * Maps API validation errors on top of what fetchDpApiResponse does.
 */
export function fetchDpApiData<T = unknown>(
	input: FetchInput,
	options: AllowEmptyOptions,
): Promise<T | undefined>;
export function fetchDpApiData<T = unknown>(
	input: FetchInput,
	options?: DisallowEmptyOptions,
): Promise<T>;
export function fetchDpApiData<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<T | undefined>;
export async function fetchDpApiData<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<T | undefined> {
	const { allowEmpty, ...requestOptions } = options ?? {};
	const response = await fetchDpApiResponse<T>(input, requestOptions);

	if (response.status === 400 && isValidationErrorsResponse(response.data)) {
		throw new ValidationError(response.data);
	}

	return unwrapResponse(response, allowEmpty);
}

/**
 * Do a Digital Platform API request and get a result type with the data or
 * error.
 */
export async function fetchDpApiResult<T = unknown>(
	input: FetchInput,
	options: AllowEmptyOptions,
): Promise<Result<T | undefined, FetchError>>;
export async function fetchDpApiResult<T = unknown>(
	input: FetchInput,
	options?: DisallowEmptyOptions,
): Promise<Result<T, FetchError>>;
export async function fetchDpApiResult<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<Result<T | undefined, FetchError>>;
export async function fetchDpApiResult<T = unknown>(
	input: FetchInput,
	options?: RequestOptions,
): Promise<Result<T | undefined, FetchError>> {
	return toResult(fetchDpApiData<T>(input, options));
}
